/**
 * Vocabulary for the Mixpanel `click` event.
 *
 * One event with a named core: `component` (which surface) and `element`
 * (what inside it) are required by `trackClick`; `source` (where the user was)
 * is set wherever the call site knows it, which is everywhere except a few
 * surfaces that render in a single place. Contextual props (`destination`,
 * `market_id`, `market_title`, `category`, `category_root`, `outcome_id`) are
 * added per call site.
 *
 * A prop name always means the same thing across components — `market_id` is
 * the market id whether the click came from a card or from the bet slip — so
 * breakdowns and filters work across every click.
 */

export const CLICK_COMPONENT = Object.freeze({
  MARKET_CARD: 'market_card',
  DEPOSIT_ENTRY: 'deposit_entry',
  LOGIN: 'login',
  REGISTER: 'register',
})

export const CLICK_ELEMENT = Object.freeze({
  // market_card
  CARD_BODY: 'card_body',
  // deposit_entry
  CTA: 'cta',
  // login / register
  SOCIAL_GOOGLE: 'social_google',
  SUBMIT: 'submit',
  GO_TO_SIGNUP: 'go_to_signup',
})

/**
 * Where the click happened. The market_card values mirror the card's
 * `renderMode`; the deposit_entry values mirror the `position` query param
 * built by `depositUrl()`, so both stay aligned with what already ships.
 */
export const CLICK_SOURCE = Object.freeze({
  FEED_MOBILE: 'feed_mobile',
  FEED_DESKTOP: 'feed_desktop',
  MARKET_MOBILE: 'market_mobile',
  MARKET_DESKTOP: 'market_desktop',
  NAVBAR: 'navbar',
  BET_SLIP: 'bet_slip',
  BET_SLIP_CONFLICT: 'bet_slip_conflict',
  CURRENCY_SELECTOR: 'currency_selector',
  CURRENCY_SELECTOR_ADD: 'currency_selector_add',
  CURRENCY_SELECTOR_EMPTY: 'currency_selector_empty',
  PORTFOLIO: 'portfolio',
  WALLET_HEADER: 'wallet_header',
  BALANCE_SUMMARY: 'balance_summary',
  NOT_ENOUGH_FUNDS: 'not_enough_funds',
  LOGIN_PAGE: 'login_page',
  SIGNUP_PAGE: 'signup_page',
})

/** MarketCard `renderMode` -> CLICK_SOURCE. */
export const RENDER_MODE_SOURCE = Object.freeze({
  'feed-mobile': CLICK_SOURCE.FEED_MOBILE,
  'feed-desktop': CLICK_SOURCE.FEED_DESKTOP,
  'market-mobile': CLICK_SOURCE.MARKET_MOBILE,
  'market-desktop': CLICK_SOURCE.MARKET_DESKTOP,
})
